import { useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogContent,
  IconButton,
  DialogTitle,
} from '@material-ui/core'
import CloseIcon from '@mui/icons-material/Close'
import { makeStyles } from '@material-ui/core/styles'

import BudgetOverview from '../../../ext_coproprietaires/BudgetOverview'
import FinancialDataHeaderCell from './FinancialDataHeaderCell'
import { isNull } from 'lodash'

const useStyles = makeStyles({
  cellSize: {
    paddingTop: '10px',
    paddingBottom: '10px',
    lineHeight: '20px',
  },
  cellNumber: {
    fontSize: '20px',
    textAlign: 'center',
  },
  cellText: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  smallCellWidth: {
    width: '200px',
  },
  hoverRow: {
    '&:hover': {
      backgroundColor: '#e9e9e9',
      cursor: 'pointer',
    },
  },
  orangeBudget: {
    color: '#ff7a00',
  },
  redBudget: {
    color: 'red',
  },
  paginationRoot: {
    fontSize: '18px',
  },
  paginationSelect: {
    minWidth: '65px',
    fontWeight: 'bold',
  },
  paginationToolbar: {
    minHeight: '64px',
    height: '64px',
  },
  paginationCaption: {
    fontSize: '18px',
  },
  paginationSelectIcon: {
    top: 'calc(50% - 12px)',
  },
})

function FinancialDataTable({
  financialData,
  page,
  rowsPerPage,
  totalRecords,
  orderBy,
  orderDirection,
  onChangePage,
  onChangeRowsPerPage,
  onChangeOrder,
}) {
  const classes = useStyles()

  const [openDialog, setOpenDialog] = useState(false)
  const [selectedCoproId, setSelectedCoproId] = useState(null)

  const handleRowClick = (coproId) => {
    setSelectedCoproId(coproId)
    setOpenDialog(true)
  }

  const handleChangePage = (event, newPage) => {
    onChangePage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    onChangeRowsPerPage(+event.target.value)
  }

  const handleOnChangeOrder = (column) => {
    const newDirection =
      orderBy === column && orderDirection === 'ASC' ? 'DESC' : 'ASC'
    onChangeOrder(column, newDirection)
  }

  const getBudgetColorClass = (percentage) => {
    if (percentage > 90) return classes.redBudget
    if (percentage >= 70 && percentage <= 90) return classes.orangeBudget
    return ''
  }

  const isValidPercentage = (percentage) => {
    if (
      percentage === 0 ||
      isNull(percentage) ||
      !isFinite(percentage) ||
      isNaN(percentage)
    ) {
      return '-'
    }
    return percentage.toFixed(1)
  }

  const selectedCoproName = financialData?.find(
    (row) => row.coproId === selectedCoproId
  )?.coproName

  return (
    <Box>
      <TablePagination
        component="div"
        classes={{
          root: classes.paginationRoot,
          toolbar: classes.paginationToolbar,
          caption: classes.paginationCaption,
          selectIcon: classes.paginationSelectIcon,
          selectRoot: classes.paginationSelect,
        }}
        count={totalRecords}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Lignes par page:"
      />
      <TableContainer component={Paper}>
        <Table>
          <TableHead style={{ background: '#143a5f' }}>
            <TableRow>
              <FinancialDataHeaderCell
                cellName="Copropriété"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onTableHeadSort={handleOnChangeOrder}
              />
              <FinancialDataHeaderCell
                cellName="Copromatic Id"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onTableHeadSort={handleOnChangeOrder}
                width={classes.smallCellWidth}
                textAlign={'center'}
              />
              <FinancialDataHeaderCell
                cellName="Etat Budgetaire"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onTableHeadSort={handleOnChangeOrder}
                width={classes.smallCellWidth}
                textAlign={'center'}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {financialData?.map((row) => {
              const percentage = isValidPercentage(
                row.condoBudgetCompletionPercentage
              )

              return (
                <TableRow
                  key={row.coproId}
                  onClick={() => handleRowClick(row.coproId)}
                  className={classes.hoverRow}
                >
                  <TableCell
                    className={`${classes.cellText} ${classes.cellSize}`}
                  >
                    {row.coproName}
                  </TableCell>
                  <TableCell
                    className={`${classes.cellNumber} ${classes.smallCellWidth} ${classes.cellSize}`}
                  >
                    {String(row.coproCopromaticId).padStart(3, '0')}
                  </TableCell>
                  <TableCell
                    className={`${classes.cellNumber} ${
                      classes.smallCellWidth
                    } ${classes.cellSize} ${getBudgetColorClass(percentage)}`}
                  >
                    {percentage === '-' ? '-' : `${percentage}%`}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        classes={{
          root: classes.paginationRoot,
          toolbar: classes.paginationToolbar,
          caption: classes.paginationCaption,
          selectIcon: classes.paginationSelectIcon,
          selectRoot: classes.paginationSelect,
        }}
        count={totalRecords}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Lignes par page:"
      />
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          style={{
            textAlign: 'center',
            color: 'white',
            background: '#183a60',
          }}
        >
          {`VUE COPROPRIETAIRE - ${selectedCoproName || ''}`}
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setOpenDialog(false)}
            style={{ position: 'absolute', top: '5px', right: '0' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BudgetOverview coproId={selectedCoproId} endPoint={'backoffice'} />
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default FinancialDataTable
